import classNames from 'classnames';
import isDeepEqual from 'lodash.isequal';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { memo, useEffect } from 'react';
import { useEntity } from '../../../../lib/entity';
import { useMessages } from '../../../../lib/messages';
import { useUser } from '../../../../lib/user';
import { getUrls } from '../../../../lib/utils/splitText';
import truncateString from '../../../../lib/utils/truncateString';
import Button from '../../../Button';
import Image from '../../../Image';
import MessageActions from './MessageActions';
import MessageHeader from './MessageHeader';
import MessageMedia from './MessageMedia';
import MessageStats from './MessageStats';
import MessageText from './MessageText';

const ReactTinyLink = dynamic(
  () => {
    return import('react-tiny-link').then((mod) => mod.ReactTinyLink);
  },
  { ssr: false },
);

const Message = ({ message, room, showInfo, thread, mainThread, side }) => {
  const router = useRouter();
  const { messageId } = router.query;

  const user = useUser().state.user;

  const entityById = useEntity().state.entities.byId;

  const messagesById = useMessages().state.messages.byId;

  const parent = messagesById[message.parent_id] ?? {};

  const getMessageRoom = () => {
    if (room.id) {
      return room;
    }

    return message.room;
  };

  const messageRoom = getMessageRoom() ?? {};

  const entity = entityById[messageRoom?.entity_id] ?? {};

  const getMessageUser = () => {
    if (message.user_id === user.id) {
      return user;
    }
    if (message.user) {
      return message.user;
    }

    return {};
  };

  const messageUser = getMessageUser();
  const profileLink = `/${messageUser.username}`;
  const messageLink = `/${messageUser.username}/m/${message.id}`;
  const urls = !message.is_deleted ? getUrls(message.message || '') : [];
  const hash = window.location.hash;
  const isHighlighted = `#m-${message.id}` === hash || messageId === `${message.id}`;

  const isThread = thread || (parent.user_id === message.user_id && messageId);

  const wrapperClassName = classNames('message', {
    'main-thread': mainThread || messageId === `${message.id}`,
    parent: !parent.id,
    child: parent.id,
    highlighted: isHighlighted,
    unavailable: message.is_deleted,
    thread: isThread,
    'parent-thread': thread,
  });

  const contentClassName = classNames('msg-wrapper');

  const photoContainerClassName = classNames('profile-pic');

  const disableClick = mainThread && messageId === `${message.id}`;

  const onPress = (eve) => {
    const selection = window.getSelection();
    if (selection.toString() || eve.target.className?.includes?.('react_tinylink')) {
      return null;
    }
    if (eve.metaKey) {
      return window.open(messageLink);
    }
    if (eve.shiftKey) {
      return window.open(
        messageLink,
        '_blank',
        'modal=yes,alwaysRaised=yes,menubar=yes,location=yes,resizable=yes,scrollbars=yes,status=yes',
      );
    }
    return router.push(messageLink);
  };

  useEffect(() => {
    if (isHighlighted && !message.is_deleted) {
      const msg = document.getElementById(`m-${message.id}`);
      if (msg) {
        msg.scrollIntoView();
      }
    }
  }, [isHighlighted, message.id, message.is_deleted]);

  if (message.is_deleted) {
    return (
      <article className={wrapperClassName}>
        <section className="info-content">This message is no longer available</section>
      </article>
    );
  }

  return (
    <>
      <article
        id={`m-${message.id}`}
        role="presentation"
        className={wrapperClassName}
        onClick={disableClick ? null : onPress}
      >
        <section className={contentClassName}>
          {(showInfo || (!mainThread && !messageId && parent.id)) && (
            <section className="info">
              <section
                className="room-link"
                role="presentation"
                onKeyPress={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {showInfo && (
                  <Button
                    as="a"
                    href={`/e/${messageRoom.type}/${entity.slug}/${messageRoom.slug}`}
                    variant="clear"
                  >
                    From room{' '}
                    <span className="link-styled">"{truncateString(messageRoom.title, 50)}"</span>
                  </Button>
                )}{' '}
                {!mainThread && !messageId && parent.id && (
                  <Button as="a" href={`#m-${parent.id}`} variant="clear">
                    {showInfo ? 'in' : 'In'} reply to{' '}
                    {parent.message && (
                      <span className="link-styled">"{truncateString(parent.message, 50)}"</span>
                    )}
                    {!parent.message && (
                      <span className="link-styled">@{parent.user.username} comment</span>
                    )}
                  </Button>
                )}
              </section>
            </section>
          )}
          <section className={photoContainerClassName}>
            <section
              role="presentation"
              className="profile-wrapper"
              onKeyPress={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Button as="a" href={profileLink} variant="clear">
                <Image
                  src={messageUser.photo}
                  className="w-full h-full object-cover"
                  alt={`${messageUser.name} profile`}
                  width={48}
                  height={48}
                  layout="fixed"
                />
              </Button>
            </section>
            {thread && <div className="thread-line" />}
          </section>
          <MessageHeader mainThread={mainThread} message={message} room={messageRoom} />
          <section className="msg-content">
            <MessageText message={message.message} />
            {!!urls.length && (
              <ReactTinyLink
                proxyUrl="https://cors.bridged.cc"
                url={urls[0]}
                showGraphic
                loadSecureUrl
              />
            )}
            <MessageMedia message={message} side={side} user={messageUser} />
            {mainThread && <MessageStats message={message} messageLink={messageLink} />}
          </section>
          <MessageActions mainThread={mainThread} message={message} />
        </section>
      </article>
    </>
  );
};

// Message.whyDidYouRender = true;

export default memo(Message, isDeepEqual);
